import "@/App.css";
import { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { BrandingProvider } from "@/context/BrandingContext";
import { ContactModalProvider } from "@/components/ContactModalProvider";
import { AIAssistantProvider } from "@/components/ai/AIAssistantProvider";
// Plugins (voir src/plugins/registry.js) : hôte vitrine + écrans admin.
import PluginHost from "@/plugins/PluginHost";
import { pluginAdminRoutes } from "@/plugins/registry";
import { Layout } from "@/components/Layout";
import RippleLoader from "@/components/RippleLoader";
import WaitingRoom from "@/components/WaitingRoom";
import SubscriptionBlocked from "@/components/SubscriptionBlocked";
import { getSubscriptionBlock, subscribeSubscriptionBlock } from "@/lib/subscriptionGate";
import { getQueueState, subscribeQueueState } from "@/lib/queueGate";
import api from "@/lib/api";
import applyPalette from "@/lib/applyPalette";
import applyTypography from "@/lib/applyTypography";
import applyShape from "@/lib/applyShape";
import applySpacing from "@/lib/applySpacing";
import applyControls from "@/lib/applyControls";
import { initStudioPreviewBridge, previewQueryParams } from "@/lib/studioPreviewBridge";
import { setThemeMeta } from "@/lib/themeMeta";
import { setLangConfig } from "@/lib/lang";
import { setRenderedThemeVersion, startThemeVersionWatch } from "@/lib/themeVersion";
import { bootstrapThemeInit } from "@/lib/bootstrap";
// Home reste en import direct : c'est la page d'entrée la plus fréquente,
// pas d'intérêt à ajouter un aller-retour de chargement supplémentaire pour elle.
import Home from "@/pages/Home";

// Cache module-level : survit aux re-montages React (navigation SPA),
// se réinitialise au rechargement complet de la page.
// Amorçage serveur (lib/bootstrap.js) : quand le HTML porte le thème, les
// jetons sont appliqués ici même, au chargement du bundle, et l'effet
// ci-dessous trouve le cache déjà rempli — aucun appel /theme/init, aucun
// premier rendu dans la palette par défaut.
let _themeInitCache = bootstrapThemeInit();

// Tout le reste est chargé à la demande (code-splitting par route) : un
// visiteur qui ne consulte que l'accueil ne télécharge plus tout l'espace
// admin, le checkout, etc. Cela réduit fortement le bundle JS initial,
// déterminant sur mobile bas de gamme / réseau lent.
const Catalog = lazy(() => import("@/pages/Catalog"));
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const OrderSuccess = lazy(() => import("@/pages/OrderSuccess"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const Account = lazy(() => import("@/pages/Account"));
const ContactConfirmation = lazy(() => import("@/pages/ContactConfirmation"));
// Pages propres au moteur de thème de la boutique (ex: "couture_v1" :
// /savoir-faire…) et page « introuvable » — voir pages/EnginePage.jsx.
const EnginePage = lazy(() => import("@/pages/EnginePage"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminProducts = lazy(() => import("@/pages/admin/AdminProducts"));
const AdminOrders = lazy(() => import("@/pages/admin/AdminOrders"));
const AdminBookings = lazy(() => import("@/pages/admin/AdminBookings"));
const AdminCategories = lazy(() => import("@/pages/admin/AdminCategories"));
const AdminStores = lazy(() => import("@/pages/admin/AdminStores"));
const AdminOnlineStore = lazy(() => import("@/pages/admin/OnlineStore"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminAiSettings = lazy(() => import("@/pages/admin/AdminAiSettings"));
const AdminGoogleTags = lazy(() => import("@/pages/admin/AdminGoogleTags"));
const AdminAiTemplates = lazy(() => import("@/pages/admin/AdminAiTemplates"));
const ThemeEditor = lazy(() => import("@/pages/admin/ThemeEditor"));
const SuperStats = lazy(() => import("@/pages/admin/SuperStats"));
const SuperRegie = lazy(() => import("@/pages/admin/SuperRegie"));
const SuperRegieOrganicVisitors = lazy(() => import("@/pages/admin/SuperRegieOrganicVisitors"));
const SuperActivity = lazy(() => import("@/pages/admin/SuperActivity"));
const SuperStripe = lazy(() => import("@/pages/admin/SuperStripe"));
const SuperSeo = lazy(() => import("@/pages/admin/SuperSeo"));
const SuperCommunication = lazy(() => import("@/pages/admin/SuperCommunication"));
const SuperBackup = lazy(() => import("@/pages/admin/SuperBackup"));
const AdminRestock = lazy(() => import("@/pages/admin/AdminRestock"));
const AdminModules = lazy(() => import("@/pages/admin/AdminModules"));
const SuperLoyalty = lazy(() => import("@/pages/admin/SuperLoyalty"));
const SuperLoyaltyCoupons = lazy(() => import("@/pages/admin/SuperLoyaltyCoupons"));
const SuperShops = lazy(() => import("@/pages/admin/SuperShops"));
const SuperQuotas = lazy(() => import("@/pages/admin/SuperQuotas"));
const MediaLibrary = lazy(() => import("@/pages/admin/MediaLibrary"));
const SuperPlugins = lazy(() => import("@/pages/admin/SuperPlugins"));
const SuperSubscriptions = lazy(() => import("@/pages/admin/SuperSubscriptions"));
const SuperPlans = lazy(() => import("@/pages/admin/SuperPlans"));
const AdminSubscription = lazy(() => import("@/pages/admin/AdminSubscription"));

const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <RippleLoader size={40} />
  </div>
);

function App() {
  // Salle d'attente publique : dès qu'un appel API renvoie un 503 "queued"
  // (capacité de la boutique atteinte — voir storefront_queue_middleware
  // côté backend et lib/api.js pour l'intercepteur qui alimente ce store),
  // tout l'arbre applicatif bascule sur <WaitingRoom/> ci-dessous plutôt que
  // de laisser des pages partiellement chargées / des appels en échec.
  const [queued, setQueued] = useState(!!getQueueState());
  useEffect(() => subscribeQueueState((state) => setQueued(!!state)), []);
  // Abonnement : dès qu'un appel répond 402 « subscription_blocked » (essai
  // terminé, abonnement échu, blocage manuel), toute l'application laisse
  // place à la page de blocage — vitrine ET back-office de la boutique. Le
  // super admin n'est jamais concerné (le serveur ne le bloque pas).
  const [blocked, setBlocked] = useState(!!getSubscriptionBlock());
  useEffect(() => subscribeSubscriptionBlock((state) => setBlocked(!!state)), []);

  useEffect(() => {
    // Pont Studio de thème (Canvas de l'éditeur) — no-op immédiat si l'app
    // n'est pas chargée dans l'iframe de prévisualisation (?studio_preview=1).
    initStudioPreviewBridge();
  }, []);

  useEffect(() => {
    // Un seul appel /theme/init groupe palette + typographie + forme +
    // espacement.
    // Si le cache module est déjà rempli (navigation SPA), on applique
    // immédiatement sans aller-retour réseau.
    const preview = previewQueryParams();
    const isPreview = Object.keys(preview).length > 0;
    if (!isPreview && _themeInitCache) {
      applyPalette(_themeInitCache.palette);
      applyTypography(_themeInitCache.typography);
      applyShape(_themeInitCache.shape);
      applySpacing(_themeInitCache.spacing);
      applyControls(_themeInitCache.controls);
      setThemeMeta({ name: _themeInitCache.name || "", productLayout: _themeInitCache.product_layout || "default", customPages: _themeInitCache.custom_pages || [], loaded: true });
      setLangConfig(_themeInitCache.i18n);
      setRenderedThemeVersion(_themeInitCache.theme_version);
      // Premier montage sur un cache amorcé par le serveur : la veille de
      // republication doit démarrer ici aussi (idempotente).
      startThemeVersionWatch();
      return;
    }
    api.get("/theme/init", { params: preview })
      .then(({ data }) => {
        if (!isPreview) _themeInitCache = data;
        applyPalette(data?.palette);
        applyTypography(data?.typography);
        applyShape(data?.shape);
        applySpacing(data?.spacing);
        applyControls(data?.controls);
        setThemeMeta({ name: data?.name || "", productLayout: data?.product_layout || "default", customPages: data?.custom_pages || [], loaded: true });
        // Module multilingue : `i18n` absent (boutique qui ne l'a pas activé,
        // ou réponse en cache antérieure à ce module) laisse le sélecteur
        // masqué et l'app strictement identique à avant.
        setLangConfig(data?.i18n);
        // Version du thème publié : surveillée ensuite pour mettre le site à
        // jour quand la boutique republie depuis le Studio (lib/themeVersion.js).
        if (!isPreview) {
          setRenderedThemeVersion(data?.theme_version);
          startThemeVersionWatch();
        }
      })
      .catch(() => {
        // /theme/init injoignable : on ne saura jamais s'il existe des pages
        // créées — ne pas laisser EnginePage attendre indéfiniment.
        setThemeMeta({ loaded: true });
      });
  }, []);

  if (queued) {
    return (
      <div className="App">
        <WaitingRoom />
      </div>
    );
  }
  if (blocked) {
    return (
      <div className="App">
        <SubscriptionBlocked />
      </div>
    );
  }

  return (
    <div className="App">
      <BrandingProvider>
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            {/* DANS le routeur : après un envoi réussi, notifySent() navigue
                vers la page de confirmation (voir ContactModalProvider). */}
            <ContactModalProvider>
            <AIAssistantProvider>
            {/* Plugins de la vitrine : démarrés après le rendu, hors du
                chemin critique, jamais bloquants (voir plugins/PluginHost.jsx). */}
            <PluginHost />
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route element={<Layout />}>
                <Route path="/" element={<Home />} />
                <Route path="/boutique" element={<Catalog />} />
                <Route path="/categorie/:categorySlug" element={<Catalog />} />
                <Route path="/produit/:slug" element={<ProductDetail />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/commande/success" element={<OrderSuccess />} />
                <Route path="/compte" element={<Account />} />
                <Route path="/contact/confirmation" element={<ContactConfirmation />} />
                {/* Attrape-tout : pages du moteur de thème (dont /confidentialite, page
                    partagée par tous les moteurs et éditable dans le Studio), sinon 404 —
                    après toutes les routes fixes, jamais avant. */}
                <Route path="*" element={<EnginePage />} />
              </Route>
              <Route path="/connexion" element={<Login />} />
              <Route path="/inscription" element={<Register />} />
              <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
              <Route path="/reinitialiser-mot-de-passe" element={<ResetPassword />} />
              {/* Route de premier niveau, HORS AdminLayout : le Studio de thème est un
                  éditeur plein écran (façon Shopify), sans la nav latérale du back-office. */}
              <Route path="/admin/theme" element={<ThemeEditor />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="produits" element={<AdminProducts />} />
                <Route path="commandes" element={<AdminOrders />} />
                <Route path="rendez-vous" element={<AdminBookings />} />
                <Route path="categories" element={<AdminCategories />} />
                <Route path="magasins" element={<AdminStores />} />
                <Route path="boutique-en-ligne" element={<AdminOnlineStore />} />
                <Route path="reglages" element={<AdminSettings />} />
                <Route path="reglages/ia" element={<AdminAiSettings />} />
                <Route path="reglages/google" element={<AdminGoogleTags />} />
                <Route path="reglages/ia/prompts" element={<AdminAiTemplates />} />
                <Route path="boutiques" element={<SuperShops />} />
                <Route path="quotas" element={<SuperQuotas />} />
                <Route path="statistiques" element={<SuperStats />} />
                <Route path="regie" element={<SuperRegie />} />
                <Route path="regie/visiteurs-organiques" element={<SuperRegieOrganicVisitors />} />
                <Route path="activite" element={<SuperActivity />} />
                <Route path="stripe" element={<SuperStripe />} />
                <Route path="communication" element={<SuperCommunication />} />
                <Route path="fidelite" element={<SuperLoyalty />} />
                <Route path="fidelite/coupons" element={<SuperLoyaltyCoupons />} />
                <Route path="seo" element={<SuperSeo />} />
                <Route path="sauvegarde" element={<SuperBackup />} />
                <Route path="restock" element={<AdminRestock />} />
                <Route path="utilisateurs" element={<AdminUsers />} />
                <Route path="modules" element={<AdminModules />} />
                <Route path="medias" element={<MediaLibrary />} />
                <Route path="plugins" element={<SuperPlugins />} />
                <Route path="abonnements" element={<SuperSubscriptions />} />
                <Route path="plans" element={<SuperPlans />} />
                <Route path="abonnement" element={<AdminSubscription />} />
                {/* Écrans admin déclarés par les plugins (chargés à la demande). */}
                {pluginAdminRoutes().map((r) => (
                  <Route key={`${r.pluginKey}:${r.path}`} path={r.path} element={<r.Component />} />
                ))}
              </Route>
            </Routes>
            </Suspense>
            </AIAssistantProvider>
            </ContactModalProvider>
          </BrowserRouter>
          <Toaster position="bottom-right" richColors />
        </CartProvider>
      </AuthProvider>
      </BrandingProvider>
    </div>
  );
}

export default App;
